import * as React from 'react';
import Lazy from 'lazy.js';
import { AppUser } from 'Src/views/App/App';
import moment from 'moment';
import {
  ColumnDefinition,
  getOrder,
  getServerFieldSectionWhere,
  isFilterExist2,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import { DATE_INTERVAL, DEFAULT_LIMIT, DEFAULT_SKIP, FILTER_TYPE, LIMIT } from 'Src/consts/table';
import { getAge, getGender } from 'Src/helpers/accessor/accessor';
import { getTwoPanelEditorFilters } from 'Src/helpers/twoPanelEditor/twoPanelEditor';
import BPromise from 'bluebird';
import propz from 'propz';
import { Loader } from 'Src/components/Loader/Loader';
import { TwoPanelEditor } from 'Src/components/TwoPanelEditor/TwoPanelEditor';
import { getAllForms } from 'Src/helpers/service/admin/forms';
import { getAllHouses } from 'Src/helpers/service/admin/houses';
import {
  getSelectOptionForAge,
  getSelectOptionForForms,
  getSelectOptionForGender,
  getSelectOptionForHouses
} from 'Src/helpers/table/select';
import { getUserGenderArrayConvertedFromEventGender } from 'Src/helpers/tournament/tournament';
import { Button } from 'Src/components/Button/Button';
import { TEAM_TYPE } from 'Src/consts/team';
import {
  getEventClash,
  getClashEventDisplayTitle,
  getStudentsMethod,
  isHousesEvent,
  isInterSchoolsEvent,
  isNonTeamSportEvent,
  isTeamOrTwoOnTwoSportEvent
} from 'Src/helpers/event/event';
import { SPORT_GENDER_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/common';
import { getEventType } from 'Src/helpers/accessor/accessor';
import { getEventStartDateAndTime } from 'Src/helpers/event/schoolEventViewHelper';
import { SchoolEventFromForm } from '../SchoolEventManager';
import {
  getSchoolStudentsWithClashesCount,
  getSchoolStudentsWithoutClashes
} from 'Src/helpers/service/admin/schoolTeamPlayers';
import { AGE_GROUPS } from 'Src/consts/school';
import { getColorClashes } from 'Src/helpers/cell/cell';
import { SchoolStudentClash } from 'Src/models/schoolStudent';
import { CLASHES } from 'Src/consts/player';
import { SchoolForm } from 'Src/models/form';
import { SchoolHouse } from 'Src/models/house';
import { EventClash, SchoolEvent } from 'Src/models/event';
import { getSchoolEvent } from 'Src/helpers/service/admin/event';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { ClashEventsModal } from 'Src/components/ClashEventsModal/ClashEventsModal';
import { resolveAgeNamingFromContext, formatAgesCompact } from 'Src/helpers/ageGroups';
import { SchoolEventParticipantsPageHelp } from '../SchoolEventParticipantsPageHelp';
import './SchoolEventAddNewTeam.scss';

interface Props {
  user: AppUser;
  currentEvent: SchoolEventFromForm;
  isSaveButtonDisabled: boolean;
  isAddTeamButtonDisabled: boolean;
  minTeams: number;
  maxTeams: number;
  onAddExistingTeamClick: (team: any) => void;
  onAddNewTeamClick: (team: any) => void;
  onUpdateTeamClick: (teamIndex: number, team: any) => void;
  onSaveClick: (team: any, isCreateAnotherEvent: boolean) => void;
  onRemoveTeamClick: (teamIndex: number) => void;
  teams: any[];
  goBack: () => void;
  participantsModeSwitch?: React.ReactNode;
}

interface State {
  items: Item[];
  itemsSelected: Item[];
  itemsCount: number;

  itemsFilters: any;
  isShowItemsFilter: boolean;
  isItemsFiltered: boolean;

  isSelectAllItemsChecked: boolean;

  sortItemsDirection: TABLE_SORT_DIRECTION;
  sortItemsColumnsName: string;

  itemCurrentPage: number;

  itemsAccumulatorInitial: Item[];
  itemsAccumulator: Item[];
  itemsAccumulatorSelected: Item[];
  itemsAccumulatorCount: number;

  itemsAccumulatorFilters: any;
  isShowItemsAccumulatorFilter: boolean;
  isItemsAccumulatorFiltered: boolean;

  isSelectAllItemsAccumulatorChecked: boolean;

  sortItemsAccumulatorDirection: TABLE_SORT_DIRECTION;
  sortItemsAccumulatorColumnsName: string;

  isLoading: boolean;
  isItemsLoading: boolean;
  isError: boolean;
  itemsSearch: string;

  itemsAddBuffer: Item[];
  itemsRemoveBuffer: Item[];

  forms: SchoolForm[];
  houses: SchoolHouse[];

  teamName: string;
  houseId: string;

  studentEventsModalText: string;
  isStudentEventsModalOpen: boolean;
  isCreateEventConfirmOpen: boolean;

  itemsHiddenFields: Set<string>;
  itemsAccumulatorHiddenFields: Set<string>;
  editingTeamIndex: number | null;
}

interface Item {
  id: string;
  permissionId: string;
  form: any;
  house: any;
  gender: string;
  firstName: string;
  lastName: string;
  clashes: {
    status: string;
    fullClashEvents: EventClash[];
    intervalClashEvents: EventClash[];
  };
  playerId?: string;
}

const ITEMS_ACCUMULATOR_COLUMNS: ColumnDefinition[] = [
  {
    text: 'SURNAME',
    field: 'lastName',
    accessor: ['lastName'],
    type: FILTER_TYPE.NONE,
    isSort: true
  },
  {
    text: 'NAME',
    field: 'firstName',
    accessor: ['firstName'],
    type: FILTER_TYPE.NONE,
    isSort: true
  },
  {
    text: 'FORM',
    field: 'forms',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['form', 'name']
  },
  {
    text: 'HOUSE',
    field: 'house',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['house', 'name']
  },
  {
    text: 'AGE',
    field: 'ages',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getAge
  },
  {
    text: 'GENDER',
    field: 'gender',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getGender
  }
];

const ITEMS_COLUMNS: ColumnDefinition[] = [
  {
    text: 'SURNAME',
    field: 'lastName',
    accessor: ['lastName'],
    type: FILTER_TYPE.TEXT,
    isSort: true
  },
  {
    text: 'NAME',
    field: 'firstName',
    accessor: ['firstName'],
    type: FILTER_TYPE.TEXT,
    isSort: true
  },
  {
    text: 'FORM',
    field: 'forms',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: ['form', 'name']
  },
  {
    text: 'HOUSE',
    field: 'houses',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: ['house', 'name']
  },
  {
    text: 'AGE',
    field: 'ages',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getAge
  },
  {
    text: 'GENDER',
    field: 'gender',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getGender
  }
];

const ITEMS_COLUMNS_WITH_CLASHES: ColumnDefinition[] = [
  {
    text: 'CLASHES',
    field: 'clashes',
    cell: getColorClashes,
    type: FILTER_TYPE.NONE,
    isSort: false
  },
  {
    text: 'SURNAME',
    field: 'lastName',
    accessor: ['lastName'],
    type: FILTER_TYPE.TEXT,
    isSort: true
  },
  {
    text: 'NAME',
    field: 'firstName',
    accessor: ['firstName'],
    type: FILTER_TYPE.TEXT,
    isSort: true
  },
  {
    text: 'FORM',
    field: 'forms',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: ['form', 'name']
  },
  {
    text: 'HOUSE',
    field: 'houses',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: ['house', 'name']
  },
  {
    text: 'AGE',
    field: 'ages',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getAge
  },
  {
    text: 'GENDER',
    field: 'gender',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getGender
  }
];

const DEFAULT_TEAM_PLAYERS_ORDER = 'firstName ASC';

const ITEMS_ACCUMULATOR_COLUMNS_WITH_CLASHES: ColumnDefinition[] = [
  {
    text: 'CLASHES',
    field: 'clashes',
    cell: getColorClashes,
    type: FILTER_TYPE.NONE,
    isSort: false
  },
  {
    text: 'SURNAME',
    field: 'lastName',
    accessor: ['lastName'],
    type: FILTER_TYPE.NONE,
    isSort: true
  },
  {
    text: 'NAME',
    field: 'firstName',
    accessor: ['firstName'],
    type: FILTER_TYPE.NONE,
    isSort: true
  },
  {
    text: 'FORM',
    field: 'forms',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['form', 'name'],
    isMobileHidden: true
  },
  {
    text: 'HOUSE',
    field: 'house',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['house', 'name'],
    isMobileHidden: true
  },
  {
    text: 'AGE',
    field: 'ages',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getAge,
    isMobileHidden: true
  },
  {
    text: 'GENDER',
    field: 'gender',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getGender,
    isMobileHidden: true
  }
];

export class SchoolEventAddNewTeam extends React.Component<Props, State> {
  private teamNamePlaceholderVariant: string;
  private scrollPositionBeforeLoading: number | null = null;
  private itemsSearchRequestId = 0;

  constructor(props: Props) {
    super(props);

    const placeholderVariants = ['A', 'B', 'First Team', 'Development', 'Select'];
    this.teamNamePlaceholderVariant = placeholderVariants[Math.floor(Math.random() * placeholderVariants.length)];

    const { currentEvent, teams } = props;
    let houseId = '';
    let teamName = '';

    if (isHousesEvent(currentEvent)) {
      const { houses } = currentEvent;
      const housesFiltered = houses.filter(house => teams.every(team => team.houseId !== house.id));
      const [house] = housesFiltered;
      houseId = house.id;
      teamName = house.name;
    }

    this.state = {
      items: [],
      itemsCount: 0,
      itemsSelected: [],

      itemsFilters: {},
      isShowItemsFilter: false,
      isItemsFiltered: false,

      isSelectAllItemsChecked: false,

      sortItemsDirection: '',
      sortItemsColumnsName: '',

      itemCurrentPage: 1,

      itemsAccumulatorInitial: [],
      itemsAccumulator: [],
      itemsAccumulatorCount: 0,
      itemsAccumulatorSelected: [],

      itemsAccumulatorFilters: {},
      isShowItemsAccumulatorFilter: false,
      isItemsAccumulatorFiltered: false,

      isSelectAllItemsAccumulatorChecked: false,

      sortItemsAccumulatorDirection: '',
      sortItemsAccumulatorColumnsName: '',

      isLoading: false,
      isItemsLoading: false,
      isError: false,
      itemsSearch: '',

      itemsAddBuffer: [],
      itemsRemoveBuffer: [],

      forms: [],
      houses: [],
      teamName: teamName,
      houseId: houseId,

      studentEventsModalText: '',
      isStudentEventsModalOpen: false,
      isCreateEventConfirmOpen: false,

      itemsHiddenFields: this.loadHiddenFields('addNewTeam_items'),
      itemsAccumulatorHiddenFields: this.loadHiddenFields('addNewTeam_accumulator'),
      editingTeamIndex: null
    };
  }

  private static ALWAYS_VISIBLE = new Set(['firstName', 'lastName', 'clashes']);

  private get storageUserId(): string {
    return this.props.user?.userId || 'anonymous';
  }

  private loadHiddenFields(key: string): Set<string> {
    const userId = this.props.user?.userId || 'anonymous';
    try {
      const raw = localStorage.getItem(`columnVisibility__${userId}__${key}`);
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return new Set<string>(parsed);
    } catch {}
    return new Set();
  }

  private saveHiddenFields(key: string, fields: Set<string>): void {
    try {
      localStorage.setItem(`columnVisibility__${this.storageUserId}__${key}`, JSON.stringify(Array.from(fields)));
    } catch {}
  }

  isColumnToggleable = (field: string): boolean => !SchoolEventAddNewTeam.ALWAYS_VISIBLE.has(field);

  onToggleItemsColumn = (field: string): void => {
    if (SchoolEventAddNewTeam.ALWAYS_VISIBLE.has(field)) return;
    this.setState(prev => {
      const next = new Set(prev.itemsHiddenFields);
      next.has(field) ? next.delete(field) : next.add(field);
      this.saveHiddenFields('addNewTeam_items', next);
      return { itemsHiddenFields: next };
    });
  };

  onToggleItemsAccumulatorColumn = (field: string): void => {
    if (SchoolEventAddNewTeam.ALWAYS_VISIBLE.has(field)) return;
    this.setState(prev => {
      const next = new Set(prev.itemsAccumulatorHiddenFields);
      next.has(field) ? next.delete(field) : next.add(field);
      this.saveHiddenFields('addNewTeam_accumulator', next);
      return { itemsAccumulatorHiddenFields: next };
    });
  };

  getDefaultItemsFilter(houseId: string) {
    const { currentEvent } = this.props;
    const { gender, ages } = currentEvent;

    const genderArrayConverted = getUserGenderArrayConvertedFromEventGender(gender);

    return isHousesEvent(currentEvent)
      ? {
          firstName: '',
          lastName: '',
          gender: genderArrayConverted,
          forms: [],
          ages: ages,
          houses: [houseId]
        }
      : {
          firstName: '',
          lastName: '',
          gender: genderArrayConverted,
          forms: [],
          ages: ages
        };
  }

  componentDidMount() {
    this.setState({
      isLoading: true
    });

    const { user } = this.props;
    const { houseId } = this.state;

    const itemsFilters = getTwoPanelEditorFilters(ITEMS_COLUMNS);
    const isShowItemsFilter = isFilterExist2(itemsFilters);
    const isItemsFiltered = isFilterExist2(itemsFilters);

    const itemsAccumulatorFilters = getTwoPanelEditorFilters(ITEMS_ACCUMULATOR_COLUMNS);
    const isShowItemsAccumulatorFilter = isFilterExist2(itemsAccumulatorFilters);
    const isItemsAccumulatorFiltered = isFilterExist2(itemsAccumulatorFilters);

    const formsPromise = getAllForms(user);
    const housesPromise = getAllHouses(user);

    BPromise.all([formsPromise, housesPromise])
      .then(([forms, houses]) => {
        const defaultItemsFilter = this.getDefaultItemsFilter(houseId);

        return new Promise<void>(resolve => {
          this.setState(
            {
              forms: forms,
              houses: houses,
              itemsFilters: defaultItemsFilter
            },
            () => resolve()
          );
        });
      })
      .then(() => {
        return this.getItemsAndItemsAccumulator();
      })
      .then(({ teamPlayersInitial, teamPlayers, teamPlayersCount, students, studentsCount }) => {
        const studentsNormalized = this.getStudentsNormalized(students);
        const teamPlayersNormalized = this.getTeamPlayersNormalized(teamPlayers);
        const teamPlayersInitialNormalized = this.getTeamPlayersNormalized(teamPlayersInitial);

        this.setState({
          items: studentsNormalized,
          itemsCount: studentsCount.count,
          isShowItemsFilter: isShowItemsFilter,
          isItemsFiltered: isItemsFiltered,

          itemsAccumulatorInitial: teamPlayersInitialNormalized,
          itemsAccumulator: teamPlayersNormalized,
          itemsAccumulatorCount: teamPlayersCount.count,
          itemsAccumulatorFilters: itemsAccumulatorFilters,
          isShowItemsAccumulatorFilter: isShowItemsAccumulatorFilter,
          isItemsAccumulatorFiltered: isItemsAccumulatorFiltered,

          isLoading: false
        });
      });
  }

  getSnapshotBeforeUpdate(prevProps: Props, prevState: State): number | null {
    if (!prevState.isLoading && this.state.isLoading && typeof window !== 'undefined') {
      return window.pageYOffset || document.documentElement.scrollTop || 0;
    }
    return null;
  }

  componentDidUpdate(prevProps: Props, prevState: State, snapshot: number | null) {
    if (snapshot !== null) {
      this.scrollPositionBeforeLoading = snapshot;
    }

    if (prevState.isLoading && !this.state.isLoading && this.scrollPositionBeforeLoading !== null) {
      const scrollTop = this.scrollPositionBeforeLoading;
      this.scrollPositionBeforeLoading = null;
      requestAnimationFrame(() => window.scrollTo({ top: scrollTop, behavior: 'auto' }));
    }

    const { teams } = this.props;
    const { teams: prevPropsTeams } = prevProps;
    if (teams.length !== prevPropsTeams.length) {
      this.setState({
        isLoading: true
      });

      const itemsFilters = getTwoPanelEditorFilters(ITEMS_COLUMNS);
      const isShowItemsFilter = isFilterExist2(itemsFilters);
      const isItemsFiltered = isFilterExist2(itemsFilters);

      const itemsAccumulatorFilters = getTwoPanelEditorFilters(ITEMS_ACCUMULATOR_COLUMNS);
      const isShowItemsAccumulatorFilter = isFilterExist2(itemsAccumulatorFilters);
      const isItemsAccumulatorFiltered = isFilterExist2(itemsAccumulatorFilters);

      this.getItemsAndItemsAccumulator().then(
        ({ teamPlayersInitial, teamPlayers, teamPlayersCount, students, studentsCount }) => {
          const studentsNormalized = this.getStudentsNormalized(students);
          const teamPlayersNormalized = this.getTeamPlayersNormalized(teamPlayers);
          const teamPlayersInitialNormalized = this.getTeamPlayersNormalized(teamPlayersInitial);

          this.setState({
            items: studentsNormalized,
            itemsCount: studentsCount.count,
            isShowItemsFilter: isShowItemsFilter,
            isItemsFiltered: isItemsFiltered,

            itemsAccumulatorInitial: teamPlayersInitialNormalized,
            itemsAccumulator: teamPlayersNormalized,
            itemsAccumulatorCount: teamPlayersCount.count,
            itemsAccumulatorFilters: itemsAccumulatorFilters,
            isShowItemsAccumulatorFilter: isShowItemsAccumulatorFilter,
            isItemsAccumulatorFiltered: isItemsAccumulatorFiltered,

            isLoading: false
          });
        }
      );
    }
  }

  getTeamPlayersNormalized(teamPlayers: any[]): Item[] {
    return teamPlayers.map(teamPlayer => {
      const { permissionId, form, gender, firstName, lastName, userId, id, house } = teamPlayer;
      return {
        permissionId,
        form,
        house,
        gender,
        firstName,
        lastName,
        id: userId,
        clashes: {
          status: CLASHES.NO_CLASH,
          fullClashEvents: [],
          intervalClashEvents: []
        },
        playerId: id
      };
    });
  }

  getStudentsNormalized(students: SchoolStudentClash[]): Item[] {
    return students.map(student => {
      const { permissionId, form, gender, firstName, lastName, id, house, clashes } = student;

      return {
        permissionId,
        form,
        house,
        gender,
        firstName,
        lastName,
        clashes,
        id
      };
    });
  }

  getItemsFilter(page, itemsAccumulator, itemsAddBuffer, isAllItems = false) {
    const {
      itemsFilters,
      forms,
      houseId,
      sortItemsColumnsName,
      sortItemsDirection,
      editingTeamIndex,
      itemsSearch
    } = this.state;
    const { teams, currentEvent } = this.props;
    const { startTime, endTime } = currentEvent;

    const {
      gender: itemsFiltersGender = [],
      ages: itemsFiltersAges = [],
      firstName: itemsFiltersFirstName = '',
      lastName: itemsFiltersLastName = '',
      forms: itemsFiltersForms = [],
      houses: itemsFiltersHouses = []
    } = itemsFilters;

    const eventDefaultFilter = this.getDefaultItemsFilter(houseId);
    const eventGender = Array.isArray(eventDefaultFilter.gender) ? eventDefaultFilter.gender : [];
    const filteredGender =
      Array.isArray(itemsFiltersGender) && itemsFiltersGender.length > 0 ? itemsFiltersGender : eventGender;

    let where: any = {};
    if (filteredGender.length > 0) {
      where.gender = { $in: filteredGender };
    }
    const teamsBlockingPlayers = teams.filter((team, index) => index !== editingTeamIndex);
    const playerUserIds = Lazy(teamsBlockingPlayers)
      .map(team => team.players)
      .flatten()
      .map(player => ({ id: player.userId }))
      .toArray();

    const allItems = [...itemsAccumulator, ...itemsAddBuffer, ...playerUserIds];
    const ninIds = Lazy(allItems)
      .map(item => item.id)
      .uniq()
      .toArray();
    propz.set(where, ['_id', '$nin'], ninIds);

    if (itemsFiltersForms.length > 0) {
      const formIds = forms.filter(form => itemsFiltersForms.includes(form.id)).map(form => form.id);

      propz.set(where, ['formId', '$in'], formIds);
    } else {
      const selectedAges = itemsFiltersAges.length > 0 ? itemsFiltersAges.map(age => Number(age)) : [];
      const ages =
        selectedAges.length > 0
          ? selectedAges
          : Array.isArray(eventDefaultFilter.ages) && eventDefaultFilter.ages.length > 0
          ? eventDefaultFilter.ages.map(age => Number(age))
          : AGE_GROUPS.ENGLISH.map((age, index) => index);
      const formIds = forms.filter(form => ages.some(age => Number(form.age) === Number(age))).map(form => form.id);

      if (formIds.length > 0) {
        propz.set(where, ['formId', '$in'], formIds);
      }
    }
    if (isHousesEvent(currentEvent)) {
      propz.set(where, ['houseId'], houseId);
    }

    if (itemsFiltersHouses.length > 0) {
      propz.set(where, ['houseId', '$in'], itemsFiltersHouses);
    }

    let and = [];

    if (itemsFiltersFirstName.length > 0) {
      and.push({ firstName: { like: itemsFiltersFirstName, options: 'i' } });
    }

    if (itemsFiltersLastName.length > 0) {
      and.push({ lastName: { like: itemsFiltersLastName, options: 'i' } });
    }

    if (itemsSearch.trim().length > 0) {
      const searchValue = itemsSearch.trim();
      and.push({
        $or: [{ firstName: { like: searchValue, options: 'i' } }, { lastName: { like: searchValue, options: 'i' } }]
      });
    }

    if (and.length > 0) {
      propz.set(where, ['$and'], and);
    }

    const startTimeCopy = new Date(startTime);
    startTimeCopy.setHours(startTime.getHours() - 1);
    const intervalClashStartDate = moment(startTimeCopy)
      .utc()
      .format();

    const endTimeCopy = new Date(endTime);
    endTimeCopy.setHours(endTime.getHours() + 1);
    const intervalClashEndDate = moment(endTimeCopy)
      .utc()
      .format();

    propz.set(where, ['intervalClashStartDate'], intervalClashStartDate);
    propz.set(where, ['intervalClashEndDate'], intervalClashEndDate);
    propz.set(where, ['fullClashStartDate'], startTime);
    propz.set(where, ['fullClashEndDate'], endTime);

    const filter = {
      where: where,
      limit: isAllItems ? DEFAULT_LIMIT : LIMIT,
      skip: isAllItems ? 0 : (page - 1) * LIMIT,
      order:
        sortItemsColumnsName && sortItemsDirection
          ? `${sortItemsColumnsName} ${sortItemsDirection}`
          : DEFAULT_TEAM_PLAYERS_ORDER
    };

    return filter;
  }

  getItemsAndItemsAccumulator() {
    const { user } = this.props;

    const filter = this.getItemsFilter(1, [], []);

    const method = getStudentsMethod(user);
    const getStudentsPromise = method(user, filter);
    const getStudentsCountPromise = getSchoolStudentsWithClashesCount(user, filter);

    return BPromise.all([getStudentsPromise, getStudentsCountPromise]).then(([students, studentsCount]) => {
      return {
        teamPlayersInitial: [],
        teamPlayers: [],
        teamPlayersCount: { count: 0 },
        students,
        studentsCount
      };
    });
  }

  onItemClick = (index: number): void => {
    const { items, itemsSelected } = this.state;
    const selectedItem = items[index];

    const selectedItemIndex = itemsSelected.findIndex(item => selectedItem.id === item.id);
    let selectedItemsUpdated = [...itemsSelected];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({
      itemsAccumulatorSelected: [],
      itemsSelected: selectedItemsUpdated
    });
  };

  onItemAccumulatorClick = (index: number): void => {
    const { itemsAccumulator, itemsAccumulatorSelected } = this.state;
    const selectedItem = itemsAccumulator[index];

    const selectedItemIndex = itemsAccumulatorSelected.findIndex(item => selectedItem.id === item.id);
    let selectedItemsUpdated = [...itemsAccumulatorSelected];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({
      itemsAccumulatorSelected: selectedItemsUpdated,
      itemsSelected: []
    });
  };

  onAddClick = () => {
    const { itemsSelected, itemsAddBuffer, itemsAccumulatorInitial, itemsAccumulator, itemCurrentPage } = this.state;
    const { user } = this.props;

    const nextItemsAddBuffer = [...itemsAddBuffer, ...itemsSelected];

    const existingIds = new Set((itemsAccumulator || []).map(x => String(x.id)));
    const nextItemsAccumulator = [...itemsAccumulator, ...itemsSelected.filter(x => !existingIds.has(String(x.id)))];

    this.setState({
      isLoading: true
    });

    const filter = this.getItemsFilter(itemCurrentPage, itemsAccumulatorInitial, nextItemsAddBuffer);
    const method = getStudentsMethod(user);
    const getStudentsPromise = method(user, filter);
    const getStudentsCountPromise = getSchoolStudentsWithClashesCount(user, filter);

    BPromise.all([getStudentsPromise, getStudentsCountPromise]).then(([students, studentsCount]) => {
      const studentsNormalized = this.getStudentsNormalized(students);

      this.setState({
        items: studentsNormalized,
        isLoading: false,
        itemsCount: studentsCount.count,
        itemsAddBuffer: nextItemsAddBuffer,
        itemsAccumulator: nextItemsAccumulator,
        itemsAccumulatorCount: nextItemsAccumulator.length,
        itemsAccumulatorSelected: [],
        itemsSelected: [],
        isSelectAllItemsAccumulatorChecked: false,
        isSelectAllItemsChecked: false
      });
    });
  };

  onRemoveClick = () => {
    const {
      itemsAccumulatorSelected,
      itemsAddBuffer,
      itemsRemoveBuffer,
      itemsAccumulatorInitial,
      itemsAccumulator,
      itemCurrentPage
    } = this.state;
    const { user } = this.props;

    const selectedIds = new Set((itemsAccumulatorSelected || []).map(x => String(x.id)));
    const addBufferIds = new Set((itemsAddBuffer || []).map(x => String(x.id)));

    const selectedExistingParticipants = (itemsAccumulatorSelected || []).filter(x => !addBufferIds.has(String(x.id)));

    const nextItemsAddBuffer = (itemsAddBuffer || []).filter(x => !selectedIds.has(String(x.id)));

    const existingRemoveIds = new Set((itemsRemoveBuffer || []).map(x => String(x.id)));
    const nextItemsRemoveBuffer = [
      ...(itemsRemoveBuffer || []),
      ...selectedExistingParticipants.filter(x => !existingRemoveIds.has(String(x.id)))
    ];

    const nextItemsAccumulatorInitial = (itemsAccumulatorInitial || []).filter(
      itemAccumulatorInitial =>
        !nextItemsRemoveBuffer.some(
          nextItemRemoveBuffer => String(nextItemRemoveBuffer.id) === String(itemAccumulatorInitial.id)
        )
    );

    const nextItemsAccumulator = (itemsAccumulator || []).filter(
      itemAccumulator => !selectedIds.has(String(itemAccumulator.id))
    );

    this.setState({
      isLoading: true
    });

    const filter = this.getItemsFilter(itemCurrentPage, nextItemsAccumulatorInitial, nextItemsAddBuffer);
    const method = getStudentsMethod(user);
    const getStudentsPromise = method(user, filter);
    const getStudentsCountPromise = getSchoolStudentsWithClashesCount(user, filter);

    BPromise.all([getStudentsPromise, getStudentsCountPromise]).then(([students, studentsCount]) => {
      const studentsNormalized = this.getStudentsNormalized(students);

      this.setState({
        items: studentsNormalized,
        isLoading: false,
        itemsCount: studentsCount.count,
        itemsAccumulatorSelected: [],
        itemsSelected: [],
        isSelectAllItemsAccumulatorChecked: false,
        isSelectAllItemsChecked: false,
        itemsAddBuffer: nextItemsAddBuffer,
        itemsRemoveBuffer: nextItemsRemoveBuffer,
        itemsAccumulatorInitial: nextItemsAccumulatorInitial,
        itemsAccumulator: nextItemsAccumulator,
        itemsAccumulatorCount: nextItemsAccumulator.length
      });
    });
  };

  onAddSingleItem = (item: Item): void => {
    const { itemsAddBuffer, itemsAccumulator, itemsCount } = this.state;
    const addBufferIds = new Set((itemsAddBuffer || []).map(x => String(x.id)));
    const accumulatorIds = new Set((itemsAccumulator || []).map(x => String(x.id)));

    const nextItemsAddBuffer = addBufferIds.has(String(item.id)) ? itemsAddBuffer : [...itemsAddBuffer, item];
    const nextItemsAccumulator = accumulatorIds.has(String(item.id)) ? itemsAccumulator : [...itemsAccumulator, item];

    this.setState(prevState => ({
      items: prevState.items.filter(x => String(x.id) !== String(item.id)),
      itemsCount: Math.max(0, itemsCount - 1),
      itemsAddBuffer: nextItemsAddBuffer,
      itemsAccumulator: nextItemsAccumulator,
      itemsAccumulatorCount: nextItemsAccumulator.length,
      itemsSelected: [],
      itemsAccumulatorSelected: [],
      isSelectAllItemsChecked: false,
      isSelectAllItemsAccumulatorChecked: false
    }));
  };

  onRemoveSingleItem = (item: Item): void => {
    const { itemsAddBuffer, itemsRemoveBuffer, itemsAccumulatorInitial, itemsAccumulator } = this.state;
    const addBufferIds = new Set((itemsAddBuffer || []).map(x => String(x.id)));
    const isNewParticipant = addBufferIds.has(String(item.id));
    const nextItemsAddBuffer = (itemsAddBuffer || []).filter(x => String(x.id) !== String(item.id));
    const nextItemsRemoveBuffer = isNewParticipant
      ? itemsRemoveBuffer
      : (itemsRemoveBuffer || []).some(x => String(x.id) === String(item.id))
      ? itemsRemoveBuffer
      : [...itemsRemoveBuffer, item];
    const nextItemsAccumulator = (itemsAccumulator || []).filter(x => String(x.id) !== String(item.id));
    const nextItemsAccumulatorInitial = (itemsAccumulatorInitial || []).filter(
      x => !nextItemsRemoveBuffer.some(removeItem => String(removeItem.id) === String(x.id))
    );

    this.setState(prevState => ({
      items: isNewParticipant ? [item, ...prevState.items] : prevState.items,
      itemsCount: isNewParticipant ? prevState.itemsCount + 1 : prevState.itemsCount,
      itemsAddBuffer: nextItemsAddBuffer,
      itemsRemoveBuffer: nextItemsRemoveBuffer,
      itemsAccumulatorInitial: nextItemsAccumulatorInitial,
      itemsAccumulator: nextItemsAccumulator,
      itemsAccumulatorCount: nextItemsAccumulator.length,
      itemsSelected: [],
      itemsAccumulatorSelected: [],
      isSelectAllItemsChecked: false,
      isSelectAllItemsAccumulatorChecked: false
    }));
  };
  onItemsFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.itemsFilters;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (filterField === 'forms') {
      nextFilters.ages = [];
    }

    if (filterField === 'ages') {
      nextFilters.forms = [];
    }

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = ITEMS_COLUMNS.find(col => col.field === filterField);
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({
      itemsFilters: nextFilters
    });
  };

  onItemsAccumulatorFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.itemsAccumulatorFilters;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = ITEMS_COLUMNS.find(col => col.field === filterField);
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({
      itemsAccumulatorFilters: nextFilters
    });
  };

  onItemsFilterClick = (event): void => {
    event.preventDefault();

    const { isShowItemsFilter } = this.state;

    this.setState({
      isShowItemsFilter: !isShowItemsFilter
    });
  };

  onItemsAccumulatorFilterClick = (event): void => {
    event.preventDefault();

    const { isShowItemsAccumulatorFilter } = this.state;

    this.setState({
      isShowItemsAccumulatorFilter: !isShowItemsAccumulatorFilter
    });
  };

  itemsSetCurrentPageParams = (currentPage: number): void => {
    const { user } = this.props;
    const { itemsAccumulator, itemsAddBuffer } = this.state;

    this.setState({
      isLoading: true
    });

    const filter = this.getItemsFilter(currentPage, itemsAccumulator, itemsAddBuffer);
    const method = getStudentsMethod(user);

    method(user, filter).then(students => {
      const studentsNormalized = this.getStudentsNormalized(students);

      this.setState({
        itemCurrentPage: currentPage,
        items: studentsNormalized,
        isLoading: false
      });
    });
  };

  itemsOnApplyFilterClick = (isSilent = false) => {
    const { itemsAccumulator, itemsAddBuffer } = this.state;
    const requestId = ++this.itemsSearchRequestId;

    if (isSilent) {
      this.setState({ isItemsLoading: true });
    } else {
      this.setState({ isLoading: true });
    }

    const filter = this.getItemsFilter(1, itemsAccumulator, itemsAddBuffer);

    const { user } = this.props;

    const method = getStudentsMethod(user);
    const getStudentsPromise = method(user, filter);
    const getStudentCountPromise = getSchoolStudentsWithClashesCount(user, filter);

    BPromise.all([getStudentsPromise, getStudentCountPromise]).then(([students, studentsCount]) => {
      if (requestId !== this.itemsSearchRequestId) return;
      const studentsNormalized = this.getStudentsNormalized(students);

      const nextState = {
        items: studentsNormalized,
        isLoading: isSilent ? this.state.isLoading : false,
        isItemsLoading: false,
        itemsCount: studentsCount.count,
        itemCurrentPage: 1,
        sortItemsDirection: '' as TABLE_SORT_DIRECTION,
        sortItemsColumnsName: ''
      };

      if (isSilent) {
        this.setState(nextState);
      } else {
        this.setState({ ...nextState, itemsAccumulatorSelected: [], itemsSelected: [] });
      }
    });
  };

  onItemsSearchChange = (itemsSearch: string): void => {
    this.setState({ itemsSearch, isItemsLoading: true }, () => this.itemsOnApplyFilterClick(true));
  };

  itemsAccumulatorOnApplyFilterClick = () => {
    const { itemsAccumulatorFilters, itemsRemoveBuffer } = this.state;
    this.setState({
      isLoading: true
    });

    let where = getServerFieldSectionWhere(itemsAccumulatorFilters);
    const ninIds = itemsRemoveBuffer.map(item => item.id);
    propz.set(where, ['id', '$nin'], ninIds);

    this.setState({
      itemsAccumulator: [],
      isLoading: false,
      itemsAccumulatorCount: 0,
      itemsAccumulatorSelected: [],
      itemsSelected: []
    });
  };

  itemsOnClearFilterClick = () => {
    const { itemsAccumulatorInitial, itemsAddBuffer, houseId } = this.state;
    const { user } = this.props;
    const defaultItemsFilter = this.getDefaultItemsFilter(houseId);

    this.setState(
      {
        isLoading: true,
        itemsFilters: defaultItemsFilter
      },
      () => {
        const filter = this.getItemsFilter(1, itemsAccumulatorInitial, itemsAddBuffer);
        const method = getStudentsMethod(user);
        const getStudentsPromise = method(user, filter);
        const getSchoolsCountPromise = getSchoolStudentsWithClashesCount(user, filter);

        BPromise.all([getStudentsPromise, getSchoolsCountPromise]).then(([students, studentsCount]) => {
          const studentsNormalized = this.getStudentsNormalized(students);

          this.setState({
            items: studentsNormalized,
            isLoading: false,
            itemsCount: studentsCount.count,
            itemsAccumulatorSelected: [],
            itemsSelected: [],
            itemCurrentPage: 1,
            sortItemsColumnsName: '',
            sortItemsDirection: ''
          });
        });
      }
    );
  };

  itemsAccumulatorOnClearFilterClick = () => {
    this.setState({
      itemsAccumulator: [],
      itemsAccumulatorCount: 0,
      itemsAccumulatorSelected: [],
      itemsSelected: [],
      itemsAccumulatorFilters: {}
    });
  };

  onSelectAllItemsClick = () => {
    const { itemsSelected, itemsAccumulator, itemsAddBuffer, itemCurrentPage } = this.state;

    this.setState({
      isLoading: true
    });

    const { user } = this.props;

    const filter = this.getItemsFilter(itemCurrentPage, itemsAccumulator, itemsAddBuffer, true);

    getSchoolStudentsWithoutClashes(user, filter).then(items => {
      const itemsFiltered = items.filter(item => {
        return itemsSelected.every(selectedItem => selectedItem.id !== item.id);
      });

      const studentsNormalizedFiltered = this.getStudentsNormalized(itemsFiltered);

      const nextSelectedItems = [...itemsSelected, ...studentsNormalizedFiltered];

      this.setState({
        itemsSelected: nextSelectedItems,
        isSelectAllItemsChecked: true,
        itemsAccumulatorSelected: [],
        isSelectAllItemsAccumulatorChecked: false,
        isLoading: false
      });
    });
  };

  onSelectAllItemsOnPageClick = () => {
    const { items, itemsSelected } = this.state;

    const itemsFiltered = items.filter(item => {
      return itemsSelected.every(selectedItem => selectedItem.id !== item.id);
    });

    const nextSelectedItems = [...itemsSelected, ...itemsFiltered];

    this.setState({
      itemsSelected: nextSelectedItems,
      isSelectAllItemsChecked: true,
      itemsAccumulatorSelected: [],
      isSelectAllItemsAccumulatorChecked: false
    });
  };

  onUnselectAllItemsClick = () => {
    this.setState({
      itemsSelected: [],
      isSelectAllItemsChecked: false,
      itemsAccumulatorSelected: [],
      isSelectAllItemsAccumulatorChecked: false
    });
  };

  onSelectAllItemsAccumulatorClick = () => {
    this.setState({
      isSelectAllItemsAccumulatorChecked: true,
      itemsSelected: [],
      isSelectAllItemsChecked: false,
      isLoading: false
    });
  };

  onSelectAllItemsAccumulatorOnPageClick = () => {
    const { itemsAccumulator, itemsAccumulatorSelected } = this.state;

    const itemsFiltered = itemsAccumulator.filter(item => {
      return itemsAccumulatorSelected.every(selectedItem => selectedItem.id !== item.id);
    });

    const nextSelectedItems = [...itemsAccumulatorSelected, ...itemsFiltered];

    this.setState({
      itemsAccumulatorSelected: nextSelectedItems,
      isSelectAllItemsAccumulatorChecked: true,
      itemsSelected: [],
      isSelectAllItemsChecked: false
    });
  };

  onUnselectAllItemsAccumulatorClick = () => {
    this.setState({
      itemsAccumulatorSelected: [],
      isSelectAllItemsAccumulatorChecked: false,
      itemsSelected: [],
      isSelectAllItemsChecked: false
    });
  };

  onSortItemsClick = (sortField: string) => {
    const { sortItemsDirection, sortItemsColumnsName, itemCurrentPage, itemsAccumulator, itemsAddBuffer } = this.state;

    const order = getOrder(sortField, sortItemsDirection, sortItemsColumnsName) as TABLE_SORT_DIRECTION;

    const filter = this.getItemsFilter(itemCurrentPage, itemsAccumulator, itemsAddBuffer);
    filter.order = `${sortField} ${order}`;
    const { user } = this.props;

    const method = getStudentsMethod(user);
    const getStudentsPromise = method(user, filter);
    const getSchoolsCountPromise = getSchoolStudentsWithClashesCount(user, filter);

    BPromise.all([getStudentsPromise, getSchoolsCountPromise]).then(([students, studentsCount]) => {
      const studentsNormalized = this.getStudentsNormalized(students);

      this.setState({
        items: studentsNormalized,
        isLoading: false,
        itemsCount: studentsCount.count,
        itemsAccumulatorSelected: [],
        itemsSelected: [],
        itemCurrentPage: 1,
        sortItemsColumnsName: sortField,
        sortItemsDirection: order
      });
    });
  };

  onSortItemsAccumulatorClick = (sortField: string) => {
    const { itemsAccumulator, sortItemsAccumulatorDirection, sortItemsAccumulatorColumnsName } = this.state;
    const order = getOrder(
      sortField,
      sortItemsAccumulatorDirection,
      sortItemsAccumulatorColumnsName
    ) as TABLE_SORT_DIRECTION;
    const direction = String(order).toUpperCase() === 'DESC' ? -1 : 1;
    const itemsSorted = [...itemsAccumulator].sort(
      (a, b) =>
        String(a[sortField] || '').localeCompare(String(b[sortField] || ''), undefined, { sensitivity: 'base' }) *
        direction
    );

    this.setState({
      itemsAccumulator: itemsSorted,
      itemsAccumulatorSelected: [],
      sortItemsAccumulatorColumnsName: sortField,
      sortItemsAccumulatorDirection: order
    });
  };

  onReplaceItemsFilters = (itemsFilters: any) => {
    this.setState({ itemsFilters }, this.itemsOnApplyFilterClick);
  };

  onAddExistingTeamButtonClick = () => {
    const { onAddExistingTeamClick, currentEvent } = this.props;
    const { teamName } = this.state;
    const team = this.getTeam();

    if (teamName.trim() || isNonTeamSportEvent(currentEvent)) {
      onAddExistingTeamClick(team);
    } else {
      this.setState({
        isError: true
      });
    }
  };

  onAddNewTeamButtonClick = () => {
    const { onAddNewTeamClick, onUpdateTeamClick, currentEvent, teams } = this.props;
    const { teamName, editingTeamIndex } = this.state;
    const team = this.getTeam();

    if (teamName.trim() || isNonTeamSportEvent(currentEvent)) {
      let houseId = '';
      let nextTeamName = '';

      if (isHousesEvent(currentEvent)) {
        const { houses } = currentEvent;
        const allTeams =
          editingTeamIndex !== null
            ? teams.map((existingTeam, index) => (index === editingTeamIndex ? team : existingTeam))
            : [...teams, team];
        const housesFiltered = houses.filter(house =>
          allTeams.every(existingTeam => existingTeam.houseId !== house.id)
        );
        const [house] = housesFiltered;
        houseId = house ? house.id : '';
        nextTeamName = house ? house.name : '';
      }

      const defaultItemsFilter = this.getDefaultItemsFilter(houseId);

      this.setState({
        items: [],
        itemsCount: 0,
        itemsSelected: [],

        itemsFilters: defaultItemsFilter,
        isShowItemsFilter: false,
        isItemsFiltered: false,

        isSelectAllItemsChecked: false,

        sortItemsDirection: '',
        sortItemsColumnsName: '',

        itemCurrentPage: 1,

        itemsAccumulatorInitial: [],
        itemsAccumulator: [],
        itemsAccumulatorCount: 0,
        itemsAccumulatorSelected: [],

        itemsAccumulatorFilters: {},
        isShowItemsAccumulatorFilter: false,
        isItemsAccumulatorFiltered: false,

        isSelectAllItemsAccumulatorChecked: false,

        sortItemsAccumulatorDirection: '',
        sortItemsAccumulatorColumnsName: '',

        isLoading: true,
        isError: false,

        itemsAddBuffer: [],
        itemsRemoveBuffer: [],

        teamName: nextTeamName,
        houseId: houseId,
        editingTeamIndex: null
      });

      if (editingTeamIndex !== null) {
        onUpdateTeamClick(editingTeamIndex, team);
      } else {
        onAddNewTeamClick(team);
      }
    } else {
      this.setState({
        isError: true
      });
    }
  };

  getTeamPlayerSnapshot(item: Item): Item {
    return {
      permissionId: item.permissionId,
      form: item.form,
      house: item.house,
      gender: item.gender,
      firstName: item.firstName,
      lastName: item.lastName,
      id: item.id,
      clashes: item.clashes || {
        status: CLASHES.NO_CLASH,
        fullClashEvents: [],
        intervalClashEvents: []
      },
      playerId: item.playerId
    };
  }

  getTeamPlayersFromTeam(team: any): Item[] {
    const playersSnapshot = propz.get(team, ['__playersSnapshot'], []);

    if (Array.isArray(playersSnapshot) && playersSnapshot.length > 0) {
      return playersSnapshot.map(player => this.getTeamPlayerSnapshot(player));
    }

    const players = propz.get(team, ['players'], []);

    return players
      .map(player => ({
        permissionId: player.permissionId,
        form: player.form || {},
        house: player.house || {},
        gender: player.gender || '',
        firstName: player.firstName || '',
        lastName: player.lastName || '',
        id: player.userId || player.id,
        clashes: player.clashes || {
          status: CLASHES.NO_CLASH,
          fullClashEvents: [],
          intervalClashEvents: []
        },
        playerId: player.id
      }))
      .filter(player => Boolean(player.id));
  }

  loadAvailableStudentsForEditor(page: number, itemsAccumulator: Item[], itemsAddBuffer: Item[]): void {
    const { user } = this.props;
    const filter = this.getItemsFilter(page, itemsAccumulator, itemsAddBuffer);
    const method = getStudentsMethod(user);
    const getStudentsPromise = method(user, filter);
    const getStudentsCountPromise = getSchoolStudentsWithClashesCount(user, filter);

    BPromise.all([getStudentsPromise, getStudentsCountPromise]).then(([students, studentsCount]) => {
      const studentsNormalized = this.getStudentsNormalized(students);

      this.setState({
        items: studentsNormalized,
        itemsCount: studentsCount.count,
        isLoading: false,
        itemCurrentPage: page,
        itemsSelected: [],
        itemsAccumulatorSelected: [],
        isSelectAllItemsChecked: false,
        isSelectAllItemsAccumulatorChecked: false
      });
    });
  }

  onEditTeamClick = (teamIndex: number): void => {
    const team = this.props.teams[teamIndex];
    if (!team) return;

    const teamPlayers = this.getTeamPlayersFromTeam(team);
    const teamHouseId = propz.get(team, ['houseId'], this.state.houseId);

    this.setState(
      {
        isLoading: true,
        editingTeamIndex: teamIndex,
        teamName: propz.get(team, ['name'], ''),
        houseId: teamHouseId,
        itemsAccumulatorInitial: [],
        itemsAccumulator: teamPlayers,
        itemsAccumulatorCount: teamPlayers.length,
        itemsAccumulatorSelected: [],
        itemsAddBuffer: teamPlayers,
        itemsRemoveBuffer: [],
        itemsSelected: [],
        itemCurrentPage: 1,
        isError: false
      },
      () => this.loadAvailableStudentsForEditor(1, [], teamPlayers)
    );
  };

  onRemoveAddedTeamClick = (event: React.MouseEvent<HTMLButtonElement>, teamIndex: number): void => {
    event.stopPropagation();

    const { currentEvent, teams, onRemoveTeamClick } = this.props;
    const { editingTeamIndex } = this.state;
    const nextTeams = teams.filter((team, index) => index !== teamIndex);

    onRemoveTeamClick(teamIndex);

    if (editingTeamIndex === teamIndex) {
      let houseId = '';
      let teamName = '';

      if (isHousesEvent(currentEvent)) {
        const housesFiltered = currentEvent.houses.filter(house => nextTeams.every(team => team.houseId !== house.id));
        const [house] = housesFiltered;
        houseId = house ? house.id : '';
        teamName = house ? house.name : '';
      }

      const defaultItemsFilter = this.getDefaultItemsFilter(houseId);

      this.setState(
        {
          isLoading: true,
          editingTeamIndex: null,
          teamName,
          houseId,
          itemsFilters: defaultItemsFilter,
          itemsAccumulatorInitial: [],
          itemsAccumulator: [],
          itemsAccumulatorCount: 0,
          itemsAccumulatorSelected: [],
          itemsAddBuffer: [],
          itemsRemoveBuffer: [],
          itemsSelected: [],
          itemCurrentPage: 1
        },
        () => this.loadAvailableStudentsForEditor(1, [], [])
      );
      return;
    }

    if (editingTeamIndex !== null && editingTeamIndex > teamIndex) {
      this.setState({ editingTeamIndex: editingTeamIndex - 1 });
    }
  };
  getTeam() {
    const { currentEvent, user } = this.props;
    const { teamName, itemsAddBuffer, houseId } = this.state;
    const playersSnapshot = itemsAddBuffer.map(item => this.getTeamPlayerSnapshot(item));
    const { activeSchoolId } = user;
    const { ages, gender, sportId } = currentEvent;

    return isHousesEvent(currentEvent)
      ? {
          ages: ages.map(age => Number(age)),
          gender: gender,
          name: teamName,
          players: itemsAddBuffer.map(item => ({
            userId: item.id,
            permissionId: item.permissionId
          })),
          __playersSnapshot: playersSnapshot,
          schoolId: activeSchoolId,
          sportId: sportId,
          teamType: TEAM_TYPE.ADHOC,
          houseId: houseId
        }
      : {
          ages: ages.map(age => Number(age)),
          gender: gender,
          name: teamName,
          players: itemsAddBuffer.map(item => ({
            userId: item.id,
            permissionId: item.permissionId
          })),
          __playersSnapshot: playersSnapshot,
          schoolId: activeSchoolId,
          sportId: sportId,
          teamType: TEAM_TYPE.ADHOC
        };
  }

  onSaveButtonClick = isCreateAnotherEvent => {
    const { onSaveClick } = this.props;
    this.setState({ isCreateEventConfirmOpen: false }, () => {
      onSaveClick(null, isCreateAnotherEvent);
    });
  };

  onOpenCreateEventConfirm = () => {
    this.setState({ isCreateEventConfirmOpen: true });
  };

  onCreateEventClick = () => {
    const { currentEvent, onSaveClick } = this.props;
    const isDirectSingleTeam = isInterSchoolsEvent(currentEvent) && !currentEvent.multiparty;
    onSaveClick(isNonTeamSportEvent(currentEvent) || isDirectSingleTeam ? this.getTeam() : null, false);
  };

  onCloseCreateEventConfirm = () => {
    this.setState({ isCreateEventConfirmOpen: false });
  };

  onChangeTeamName = event => {
    const teamName = event.target.value;

    this.setState({
      teamName,
      isError: false
    });
  };

  onHouseChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const houseId = event.target.value;
    const house = this.props.currentEvent.houses.find(item => item.id === houseId);
    const itemsFilters = this.getDefaultItemsFilter(houseId);

    this.setState(
      {
        houseId,
        teamName: house ? house.name : '',
        itemsFilters,
        items: [],
        itemsCount: 0,
        itemsSelected: [],
        itemsAccumulatorInitial: [],
        itemsAccumulator: [],
        itemsAccumulatorCount: 0,
        itemsAccumulatorSelected: [],
        itemsAddBuffer: [],
        itemsRemoveBuffer: [],
        itemCurrentPage: 1,
        isLoading: true,
        isError: false
      },
      () => {
        this.loadAvailableStudentsForEditor(1, [], []);
      }
    );
  };

  onClashClick = (student: SchoolStudentClash) => {
    const { user } = this.props;
    const { clashes, firstName, lastName } = student;
    const { fullClashEvents, intervalClashEvents } = clashes;
    const fullClashEventIds = fullClashEvents.map(fullClashEvent => fullClashEvent._id);
    const intervalClashEventIds = intervalClashEvents.map(intervalClashEvent => intervalClashEvent._id);

    const allPlayerEventIds = [...fullClashEventIds, ...intervalClashEventIds];
    const allPlayerEventIdsUniq = Lazy(allPlayerEventIds)
      .uniq()
      .toArray();

    this.setState({
      isLoading: true
    });

    const eventsPromise: Promise<SchoolEvent>[] = allPlayerEventIdsUniq.map(eventId => getSchoolEvent(user, eventId));

    BPromise.all(eventsPromise).then(events => {
      const eventNames = events
        .map((event, index) => {
          const title = getClashEventDisplayTitle(event, user);
          const clash = getEventClash(event, fullClashEventIds, intervalClashEventIds);
          return `${index + 1}. ${title} ${clash}`;
        })
        .join('\n ');

      this.setState({
        isLoading: false,
        studentEventsModalText: `${firstName} ${lastName} is participating in the following events: \n${eventNames}`,
        isStudentEventsModalOpen: true
      });
    });
  };

  onCloseStudentEventsModalClick = () => {
    this.setState({
      isStudentEventsModalOpen: false
    });
  };

  renderStudentEventsModal(): React.ReactNode {
    const { isStudentEventsModalOpen, studentEventsModalText } = this.state;
    return (
      <ClashEventsModal
        isOpen={isStudentEventsModalOpen}
        text={studentEventsModalText}
        onClose={this.onCloseStudentEventsModalClick}
      />
    );
  }

  renderDsIcon(path: string, size = 20): React.ReactNode {
    return (
      <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" focusable="false">
        <path d={path} />
      </svg>
    );
  }

  getTeamNamePlaceholder(eventSportName: string, eventGender: string, eventAges: string): string {
    const genderPart = eventGender
      ? eventGender.replace(/Female(?: only)?/i, 'Girls').replace(/Male(?: only)?/i, 'Boys')
      : '';
    const agePart = eventAges && eventAges !== 'All ages' ? eventAges.split(',')[0].trim() : '';
    return ['e.g.', agePart, genderPart, eventSportName, this.teamNamePlaceholderVariant].filter(Boolean).join(' ');
  }

  renderCreateEventConfirmModal(): React.ReactNode {
    const { isCreateEventConfirmOpen } = this.state;
    const { teams } = this.props;
    const playersCount = teams.reduce((total, team) => total + propz.get(team, ['players', 'length'], 0), 0);

    if (!isCreateEventConfirmOpen) {
      return null;
    }

    return (
      <div className="eSchoolEventAddNewTeam__confirmOverlay" role="dialog" aria-modal="true">
        <div className="eSchoolEventAddNewTeam__confirmModal">
          <div className="eSchoolEventAddNewTeam__confirmIcon">
            {this.renderDsIcon('M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z', 24)}
          </div>
          <div className="eSchoolEventAddNewTeam__confirmTitle">Create event</div>
          <div className="eSchoolEventAddNewTeam__confirmText">
            This will create the fixture with {teams.length} team{teams.length === 1 ? '' : 's'} and {playersCount}{' '}
            player
            {playersCount === 1 ? '' : 's'}.
          </div>
          <div className="eSchoolEventAddNewTeam__confirmActions">
            <button
              type="button"
              className="eSchoolEventAddNewTeam__btnCancelDs"
              onClick={this.onCloseCreateEventConfirm}
            >
              Cancel
            </button>
            <button
              type="button"
              className="eSchoolEventAddNewTeam__btnGhostDs"
              onClick={() => this.onSaveButtonClick(true)}
            >
              Create another event
            </button>
            <button
              type="button"
              className="eSchoolEventAddNewTeam__btnPrimaryDs"
              onClick={() => this.onSaveButtonClick(false)}
            >
              Go to event
            </button>
          </div>
        </div>
      </div>
    );
  }

  renderTeamsInFixture(): React.ReactNode {
    const { teams } = this.props;
    const { editingTeamIndex } = this.state;

    if (!teams.length) {
      return null;
    }

    return (
      <section className="eSchoolEventAddNewTeam__teamsCard" data-help-anchor="event-participants-added-teams">
        <div className="eSchoolEventAddNewTeam__teamsHead">
          <span className="eSchoolEventAddNewTeam__teamsIcon">
            {this.renderDsIcon(
              'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
              18
            )}
          </span>
          <div>
            <div className="eSchoolEventAddNewTeam__teamsTitle">Teams in this fixture</div>
            <div className="eSchoolEventAddNewTeam__teamsSubtitle">{teams.length} added</div>
          </div>
        </div>
        <div className="eSchoolEventAddNewTeam__teamsList">
          {teams.map((team, index) => {
            const playersCount = Number(propz.get(team, ['players', 'length'], 0));
            return (
              <div
                key={`${team.name}-${index}`}
                className={`eSchoolEventAddNewTeam__teamRow ${
                  editingTeamIndex === index ? 'eSchoolEventAddNewTeam__teamRow--active' : ''
                }`}
                role="button"
                tabIndex={0}
                onClick={() => this.onEditTeamClick(index)}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    this.onEditTeamClick(index);
                  }
                }}
              >
                <span className="eSchoolEventAddNewTeam__teamIcon">
                  {this.renderDsIcon('M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z', 17)}
                </span>
                <div className="eSchoolEventAddNewTeam__teamMain">
                  <div className="eSchoolEventAddNewTeam__teamName">{team.name}</div>
                  <div className="eSchoolEventAddNewTeam__teamMeta">
                    {playersCount} player{playersCount === 1 ? '' : 's'} · new team
                  </div>
                </div>
                <button
                  type="button"
                  className="eSchoolEventAddNewTeam__teamRemove"
                  aria-label="Remove team"
                  onClick={event => this.onRemoveAddedTeamClick(event, index)}
                >
                  {this.renderDsIcon('M18 6 6 18M6 6l12 12', 15)}
                </button>
              </div>
            );
          })}
        </div>
      </section>
    );
  }
  render() {
    const {
      items,
      isLoading,
      itemsSelected,
      itemsAccumulatorSelected,
      itemsAccumulator,
      itemsFilters,
      isShowItemsFilter,
      isItemsFiltered,
      itemsCount,
      itemsAccumulatorCount,
      itemCurrentPage,
      itemsAccumulatorFilters,
      isShowItemsAccumulatorFilter,
      isItemsAccumulatorFiltered,
      isSelectAllItemsChecked,
      isSelectAllItemsAccumulatorChecked,
      sortItemsDirection,
      sortItemsColumnsName,
      sortItemsAccumulatorDirection,
      sortItemsAccumulatorColumnsName,
      itemsAddBuffer,
      itemsRemoveBuffer,
      forms,
      houses,
      isError,
      teamName,
      houseId,
      isStudentEventsModalOpen,
      itemsHiddenFields,
      itemsAccumulatorHiddenFields,
      editingTeamIndex
    } = this.state;

    if (isLoading && items.length === 0 && itemsAccumulator.length === 0) {
      return <Loader />;
    }

    const {
      user,
      isSaveButtonDisabled,
      isAddTeamButtonDisabled,
      minTeams,
      maxTeams,
      currentEvent,
      teams,
      goBack
    } = this.props;
    const { activeSchool } = user;
    const { ageGroupsNaming, isShowStudentEventClashes } = activeSchool;
    const { houses: eventHouses } = currentEvent;
    const selectedHouse = eventHouses.find(house => house.id === houseId);

    const allItemsColumns = isShowStudentEventClashes ? ITEMS_COLUMNS_WITH_CLASHES : ITEMS_COLUMNS;
    const allAccumulatorColumns = isShowStudentEventClashes
      ? ITEMS_ACCUMULATOR_COLUMNS_WITH_CLASHES
      : ITEMS_ACCUMULATOR_COLUMNS;
    const visibleItemsColumns = allItemsColumns.filter(col => !itemsHiddenFields.has(col.field));
    const visibleAccumulatorColumns = allAccumulatorColumns.filter(col => !itemsAccumulatorHiddenFields.has(col.field));

    const eventStartDateAndTimeFormatted = getEventStartDateAndTime(currentEvent);
    const eventSportName = propz.get(currentEvent, ['sport', 'name'], '');
    const eventGender = SPORT_GENDER_SERVER_TO_CLIENT_MAPPING[currentEvent.gender];
    const eventAges =
      formatAgesCompact(propz.get(currentEvent, ['ages'], []), resolveAgeNamingFromContext(user, currentEvent)) ||
      'All ages';
    const eventStatus = getEventType(currentEvent);

    const itemsFiltersOptions = {
      ages: getSelectOptionForAge(forms, ageGroupsNaming),
      gender: getSelectOptionForGender(),
      forms: getSelectOptionForForms(forms),
      houses: getSelectOptionForHouses(houses)
    };
    const eventGenderPreset = getUserGenderArrayConvertedFromEventGender(currentEvent.gender);
    const itemsPresetFilters = {
      ages: (currentEvent.ages || []).map(age => String(age)),
      gender: eventGenderPreset.length === 1 ? eventGenderPreset.map(String) : []
    };

    const itemsAccumulatorFiltersOptions = {};

    const isIndividual = isNonTeamSportEvent(currentEvent);
    const isDirectSingleTeam = isInterSchoolsEvent(currentEvent) && !currentEvent.multiparty;
    const isCurrentTeamPending =
      !isIndividual &&
      (editingTeamIndex !== null || itemsAccumulator.length > 0 || Boolean(teamName.trim()));
    const isCurrentTeamReady =
      !isIndividual &&
      Boolean(teamName.trim()) &&
      (!isAddTeamButtonDisabled || editingTeamIndex !== null);
    const hasUnstagedTeam =
      !isIndividual &&
      !isDirectSingleTeam &&
      (editingTeamIndex !== null || itemsAccumulator.length > 0 || Boolean(teamName.trim()));
    const isTeamBuilderLocked =
      !isIndividual && !isDirectSingleTeam && isAddTeamButtonDisabled && editingTeamIndex === null;
    const baseCreateEnabled = isIndividual
      ? true
      : isDirectSingleTeam
      ? Boolean(teamName.trim())
      : teams.length > 0 && !isSaveButtonDisabled;
    const isCreateEnabled = baseCreateEnabled && !hasUnstagedTeam;
    const actionStatus = isIndividual
      ? `${itemsAccumulator.length} selected · participants optional`
      : isDirectSingleTeam
      ? `${itemsAccumulator.length} ${itemsAccumulator.length === 1 ? 'player' : 'players'} in current team`
      : hasUnstagedTeam
      ? editingTeamIndex !== null
        ? 'Update the edited team before creating'
        : 'Add the current team before creating'
      : teams.length < minTeams
      ? `${teams.length} of ${minTeams} required teams added`
      : `${teams.length} of maximum ${maxTeams} teams added`;
    const createDisabledReason = isCreateEnabled
      ? ''
      : isDirectSingleTeam && !teamName.trim()
      ? 'Enter a team name to continue.'
      : !isIndividual && !isDirectSingleTeam && !teamName.trim() && (hasUnstagedTeam || teams.length < minTeams)
      ? `Enter a team name, then click Add team to fixture${minTeams > 1 ? ` until ${minTeams} teams have been added` : ''}.`
      : !isIndividual && !isDirectSingleTeam && hasUnstagedTeam
      ? editingTeamIndex !== null
        ? 'Click Update team in fixture before continuing.'
        : 'Click Add team to fixture before continuing.'
      : teams.length < minTeams
      ? `Add at least ${minTeams} team${minTeams === 1 ? '' : 's'} to continue.`
      : 'Complete the current team before continuing.';
    const classes = [
      'mt-3',
      'eSchoolEventAddNewTeam',
      isIndividual ? 'eSchoolEventAddNewTeam--individual' : 'eSchoolEventAddNewTeam--team',
      isStudentEventsModalOpen ? 'modal-open' : ''
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={classes}>
        {this.renderStudentEventsModal()}
        <SchoolEventParticipantsPageHelp
          mode="new"
          user={user}
          currentEvent={currentEvent}
          availableCount={itemsCount}
          selectedCount={itemsAccumulatorCount}
          teamsCount={teams.length}
          minTeams={minTeams}
          maxTeams={maxTeams}
          isCreateEnabled={isCreateEnabled}
          isAddTeamDisabled={isAddTeamButtonDisabled}
          isCurrentTeamPending={isCurrentTeamPending}
          isCurrentTeamReady={isCurrentTeamReady}
          teamName={teamName}
          houseName={selectedHouse ? selectedHouse.name : ''}
          isEditingTeam={editingTeamIndex !== null}
        />

        <section className="eSchoolEventAddNewTeam__buildCard">
          <div className="eSchoolEventAddNewTeam__buildHead">
            <span className="eSchoolEventAddNewTeam__buildIcon">
              {this.renderDsIcon(
                isIndividual
                  ? 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75'
                  : 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
                19
              )}
            </span>
            <div>
              <h2 className="eSchoolEventAddNewTeam__buildTitle">
                {isIndividual ? 'Select participants' : 'Build a new team'}
              </h2>
              <div className="eSchoolEventAddNewTeam__buildSubtitle">
                {isIndividual
                  ? 'Pick the students taking part — move them to the selected panel or double-click a row.'
                  : 'Name the team and pick its players. Add several teams to the same fixture.'}
              </div>
            </div>
            {this.props.participantsModeSwitch}
          </div>

          <div className="eSchoolEventAddNewTeam__divider" />

          <div className="eSchoolEventAddNewTeam__buildBody">
            {isTeamBuilderLocked ? (
              <div className="eSchoolEventAddNewTeam__limitNotice" data-help-anchor="event-participants-stage-team">
                <strong>Team limit reached</strong>
                <span>
                  This event already has the maximum of {maxTeams} {maxTeams === 1 ? 'team' : 'teams'}. Edit or remove a
                  staged team below, or create the event.
                </span>
              </div>
            ) : (
              <>
                {isHousesEvent(currentEvent) && (
                  <div className="eSchoolEventAddNewTeam__fieldGroup" data-help-anchor="event-participants-house">
                    <label className="eSchoolEventAddNewTeam__label">House</label>
                    <div className="eSchoolEventAddNewTeam__houseControl">
                      <span className="eSchoolEventAddNewTeam__houseControlIcon" aria-hidden="true">
                        {selectedHouse && selectedHouse.pic ? (
                          <img className="eSchoolEventAddNewTeam__houseLogo" src={selectedHouse.pic} alt="" />
                        ) : (
                          <span className="eSchoolEventAddNewTeam__houseLogoFallback">
                            {selectedHouse && selectedHouse.name ? selectedHouse.name.charAt(0) : 'H'}
                          </span>
                        )}
                      </span>
                      <select
                        className="eSchoolEventAddNewTeam__input eSchoolEventAddNewTeam__input--compact eSchoolEventAddNewTeam__houseSelect"
                        value={houseId}
                        onChange={this.onHouseChange}
                      >
                        {eventHouses
                          .filter(house =>
                            teams.every((team, index) => index === editingTeamIndex || team.houseId !== house.id)
                          )
                          .map(house => (
                            <option key={house.id} value={house.id}>
                              {house.name}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>
                )}

                {!isIndividual && isTeamOrTwoOnTwoSportEvent(currentEvent) && (
                  <div
                    className="eSchoolEventAddNewTeam__teamNameBlock"
                    data-help-anchor="event-participants-team-name"
                  >
                    <label className="eSchoolEventAddNewTeam__label">
                      Team name <span className="eSchoolEventAddNewTeam__required">*</span>
                    </label>
                    <input
                      className="eSchoolEventAddNewTeam__input eSchoolEventAddNewTeam__input--compact"
                      type="text"
                      placeholder={this.getTeamNamePlaceholder(eventSportName, eventGender, eventAges)}
                      onChange={this.onChangeTeamName}
                      value={teamName}
                    />
                    {isError && <div className="eSchoolEventAddNewTeam__fieldError">Enter team name</div>}
                  </div>
                )}

                <div className="eSchoolEventAddNewTeam__editorWrap" data-help-anchor="event-participants-students">
                  <TwoPanelEditor
                    isSourceFirst
                    itemsPresetFilters={itemsPresetFilters}
                    onReplaceItemsFilters={this.onReplaceItemsFilters}
                    onItemsSearchChange={this.onItemsSearchChange}
                    itemsSearchValue={this.state.itemsSearch}
                    isItemsLoading={this.state.isItemsLoading}
                    items={items}
                    itemsTitle={'Available students'}
                    itemsColumns={visibleItemsColumns}
                    itemsSelected={itemsSelected}
                    onItemClick={this.onItemClick}
                    onItemsFilterChange={this.onItemsFilterChange}
                    itemsFilters={itemsFilters}
                    itemsFiltersOptions={itemsFiltersOptions}
                    isShowItemsFilter={isShowItemsFilter}
                    isItemsFiltered={isItemsFiltered}
                    onItemsFilterClick={this.onItemsFilterClick}
                    itemsAccumulator={itemsAccumulator}
                    itemsAccumulatorTitle={'Selected students'}
                    itemsAccumulatorColumns={visibleAccumulatorColumns}
                    onItemAccumulatorClick={this.onItemAccumulatorClick}
                    itemsAccumulatorSelected={itemsAccumulatorSelected}
                    onItemsAccumulatorFilterChange={this.onItemsAccumulatorFilterChange}
                    itemsAccumulatorFilters={itemsAccumulatorFilters}
                    itemsAccumulatorFiltersOptions={itemsAccumulatorFiltersOptions}
                    isShowItemsAccumulatorFilter={isShowItemsAccumulatorFilter}
                    isItemsAccumulatorFiltered={isItemsAccumulatorFiltered}
                    onItemsAccumulatorFilterClick={this.onItemsAccumulatorFilterClick}
                    onAddClick={this.onAddClick}
                    onRemoveClick={this.onRemoveClick}
                    onAddSingleItem={this.onAddSingleItem}
                    onRemoveSingleItem={this.onRemoveSingleItem}
                    itemsCount={itemsCount}
                    itemsAccumulatorCount={itemsAccumulatorCount}
                    itemAccumulatorCurrentPage={1}
                    itemCurrentPage={itemCurrentPage}
                    itemsSetCurrentPageParams={this.itemsSetCurrentPageParams}
                    itemsOnApplyFilterClick={this.itemsOnApplyFilterClick}
                    itemsOnClearFilterClick={this.itemsOnClearFilterClick}
                    itemsAccumulatorOnApplyFilterClick={this.itemsAccumulatorOnApplyFilterClick}
                    itemsAccumulatorOnClearFilterClick={this.itemsAccumulatorOnClearFilterClick}
                    onSelectAllItemsClick={this.onSelectAllItemsClick}
                    onSelectAllItemsOnPageClick={this.onSelectAllItemsOnPageClick}
                    onUnselectAllItemsClick={this.onUnselectAllItemsClick}
                    isSelectAllItemsChecked={isSelectAllItemsChecked}
                    onSelectAllItemsAccumulatorClick={this.onSelectAllItemsAccumulatorClick}
                    onSelectAllItemsAccumulatorOnPageClick={this.onSelectAllItemsAccumulatorOnPageClick}
                    onUnselectAllItemsAccumulatorClick={this.onUnselectAllItemsAccumulatorClick}
                    isSelectAllItemsAccumulatorChecked={isSelectAllItemsAccumulatorChecked}
                    sortItemsDirection={sortItemsDirection}
                    sortItemsColumnsName={sortItemsColumnsName}
                    onSortItemsClick={this.onSortItemsClick}
                    sortItemsAccumulatorDirection={sortItemsAccumulatorDirection}
                    sortItemsAccumulatorColumnsName={sortItemsAccumulatorColumnsName}
                    onSortItemsAccumulatorClick={this.onSortItemsAccumulatorClick}
                    itemsAddBuffer={itemsAddBuffer}
                    itemsRemoveBuffer={itemsRemoveBuffer}
                    onCellItemsClick={this.onClashClick}
                    //column visibility – items panel
                    itemsAllColumns={allItemsColumns}
                    itemsHiddenFields={itemsHiddenFields}
                    onToggleItemsColumn={this.onToggleItemsColumn}
                    isItemsColumnToggleable={this.isColumnToggleable}
                    //column visibility – accumulator panel
                    itemsAccumulatorAllColumns={allAccumulatorColumns}
                    itemsAccumulatorHiddenFields={itemsAccumulatorHiddenFields}
                    onToggleItemsAccumulatorColumn={this.onToggleItemsAccumulatorColumn}
                    isItemsAccumulatorColumnToggleable={this.isColumnToggleable}
                  />
                </div>

                {!isIndividual && !isDirectSingleTeam && (
                  <div className="eSchoolEventAddNewTeam__addTeamRow" data-help-anchor="event-participants-stage-team">
                    <button
                      type="button"
                      className="eSchoolEventAddNewTeam__btnAddTeam"
                      onClick={this.onAddNewTeamButtonClick}
                      disabled={!isCurrentTeamReady}
                    >
                      {this.renderDsIcon(editingTeamIndex === null ? 'M12 5v14M5 12h14' : 'M20 6 9 17l-5-5', 15)}
                      <span>{editingTeamIndex === null ? 'Add team to fixture' : 'Update team in fixture'}</span>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {!isIndividual && this.renderTeamsInFixture()}

        <div className="eSchoolEventAddNewTeam__actionsDs" data-help-anchor="event-participants-actions">
          <button type="button" className="eSchoolEventAddNewTeam__btnBackDs" onClick={goBack}>
            {this.renderDsIcon('M19 12H5M11 18l-6-6 6-6', 15)}
            <span>Back to details</span>
          </button>

          <div className="eSchoolEventAddNewTeam__actionsSpacer" />

          <div className="eSchoolEventAddNewTeam__continueGroup">
            {!isCreateEnabled ? <div className="eSchoolEventAddNewTeam__disabledReason" role="status">{createDisabledReason}</div> : null}
            <div className="eSchoolEventAddNewTeam__continueRow">
              <span className="eSchoolEventAddNewTeam__teamsCount">{actionStatus}</span>
              <button
                type="button"
                className="eSchoolEventAddNewTeam__btnCreateDs"
                disabled={!isCreateEnabled}
                onClick={this.onCreateEventClick}
              >
                <span>Create event</span>
                {this.renderDsIcon('M20 6 9 17l-5-5', 15)}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
